// Single entry point that decides V1 OpenContactLeadPopup vs FollowUpV2
// based on the practice's is_followup_v2_enabled preference flag.
//
// Why this exists: before this component, ~9 call sites in the FE mounted
// <OpenContactLeadPopup> directly. To roll FollowUpV2 out per-practice we'd
// have had to add the same `useFollowUpV2 ? <FollowUpV2/> : <OpenContactLeadPopup/>`
// swap to each of them — error-prone, easy to forget one, and would
// duplicate the gate logic 9 times. This component centralises the swap:
// every callsite renders <ContactLeadPopupRouter /> and the gate logic lives
// here in one file.
//
// Prop contract: accept the V1 popup's full prop surface (which is a
// superset). Pass through verbatim to V1. For V2, translate to its lean
// shape (open / onClose / leadDetail / practiceId / handleReloadLeads).
//
// Adding a new prop V2 also cares about: add it to the destructure +
// forward it inside the V2 branch.

/* eslint-disable react/prop-types */
import { useEffect } from 'react';
import { dispatch, useSelector } from 'src/redux/store';
import { getPracticeItem } from 'src/redux/slices/practice';
import useIsFollowUpV2Practice from 'src/hooks/useIsFollowUpV2Practice';
import FollowUpV2 from 'src/sections/@dashboard/leadDetail/FollowUpV2';
import { resolveContactPracticeId } from 'src/utils/contactPracticeContext';
import OpenContactLeadPopup from './OpenContactLeadPopup';

export default function ContactLeadPopupRouter({
    // Shared between V1 and V2 — same shape
    open,
    onClose,
    handleReloadLeads,
    practiceId,
    // V1 uses `user`, V2 uses `leadDetail` — they're the same lead object
    user,
    leadDetail,
    // Optional diagnostic-only label identifying the launch site. Threaded to
    // both children and surfaced by ContactPopupDebugBox (superadmin only).
    // Defaults to 'unknown' so existing call sites work unchanged.
    debugSource = 'unknown',
    // Stable treatment anchor for multi-treatment leads. When provided, FollowUpV2
    // uses it as the active-treatment anchor on first render instead of inferring
    // it late from leadDetail — prevents the wrong-treatment flash. Optional;
    // defaults null so existing call sites are unchanged.
    leadTreatmentId = null,
    // Called once the popup is really on screen (the V1/V2 flag is known): a caller that locks page
    // scroll for it locks only then, so a popup that never mounts can never leave the page frozen.
    onReady,
    // Everything else is V1-only and forwarded as a rest spread (steps,
    // type, validStep, BookingAttendance, updateStep, inputRef, active,
    // setIsRender, selectedRow, setSelectedRow, openArchivePopup,
    // headerButtons, archiveOpen, currentTab, leadDetailTaskPageFilter, …)
    ...v1Only
}) {
    const popupLead = leadDetail || user;
    // A caller that passes neither a lead with a practice nor a practiceId (the call flows' lead
    // rows carry none) would leave the V1/V2 flag "loading" for ever and render nothing. The
    // active practice is the honest fallback: the popup is only ever opened inside it.
    const activePracticeId = useSelector((state) => state.practiceTreatmentList?.practiceId);
    const resolvedPracticeId = resolveContactPracticeId({ lead: popupLead, practiceId: practiceId ?? activePracticeId });
    const useFollowUpV2 = useIsFollowUpV2Practice(resolvedPracticeId);

    // SELF-HEAL: useIsFollowUpV2Practice() returns null when practice preferences
    // aren't in Redux for this surface. Some surfaces (e.g. the tasks-v2 Task Detail
    // page) never dispatch getPracticeItem, so the flag would stay null forever and
    // the router would stay invisible. Trigger the practice load so the flag resolves.
    // GET-deduped by callApi; gated to fire only while open + unresolved.
    useEffect(() => {
        if (open && useFollowUpV2 === null && resolvedPracticeId) {
            dispatch(getPracticeItem(resolvedPracticeId));
        }
    }, [open, useFollowUpV2, resolvedPracticeId]);

    const popupReady = open && useFollowUpV2 !== null;
    useEffect(() => {
        if (popupReady) onReady?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [popupReady]);

    if (!open) return null;

    // PREFERENCES-LOADING GUARD: useIsFollowUpV2Practice() returns null while the
    // practice preferences haven't resolved yet (initial page load) or during the
    // post-practice-switch stale guard. Return null (invisible) instead of a spinner
    // Dialog — the ~1-2 s wait was visible as a white box flash on the Overview tab.
    // The useEffect above triggers getPracticeItem which resolves the flag quickly;
    // once it becomes true/false this component re-renders with the correct popup.
    if (useFollowUpV2 === null) return null;

    if (useFollowUpV2) {
        return (
            <FollowUpV2
                open={open}
                onClose={onClose}
                leadDetail={popupLead}
                practiceId={resolvedPracticeId}
                handleReloadLeads={handleReloadLeads}
                debugSource={debugSource}
                leadTreatmentId={leadTreatmentId}
            />
        );
    }

    return (
        <OpenContactLeadPopup
            open={open}
            onClose={onClose}
            user={user || leadDetail}
            handleReloadLeads={handleReloadLeads}
            debugSource={debugSource}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...v1Only}
            practiceId={resolvedPracticeId}
        />
    );
}
